import React from 'react';
import { Envelope, Spinner, ArrowsClockwise, CheckCircle, Warning } from '@phosphor-icons/react';

export const InboundEmailCard = ({ inbound, verify, verifying, verifySetup }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 mb-8" data-testid="admin-inbound-card">
    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
      <h2 className="font-outfit text-xl font-semibold text-[#09090B] flex items-center gap-2"><Envelope size={20} className="text-[#002FA7]" /> Inbound Email</h2>
      <button data-testid="verify-inbound-button" onClick={verifySetup} disabled={verifying}
        className="flex items-center gap-1.5 px-4 py-2 text-sm bg-[#002FA7] text-white font-ibm font-semibold rounded-md hover:bg-[#00237D] disabled:opacity-50 transition-all">
        {verifying ? <><Spinner size={14} className="animate-spin" /> Checking DNS...</> : <><ArrowsClockwise size={14} /> Verify inbound setup</>}
      </button>
    </div>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
      <div data-testid="inbound-routed-stat">
        <p className="text-xs font-bold tracking-[0.12em] uppercase text-[#6B7280] font-ibm">Routed to Cases</p>
        <p className="font-outfit text-2xl font-bold text-green-600">{inbound?.routed_to_cases ?? 0}</p>
      </div>
      <div data-testid="inbound-pending-stat">
        <p className="text-xs font-bold tracking-[0.12em] uppercase text-[#6B7280] font-ibm">Pending in Inboxes</p>
        <p className="font-outfit text-2xl font-bold text-amber-600">{inbound?.pending_inbox ?? 0}</p>
      </div>
      <div data-testid="inbound-assigned-stat">
        <p className="text-xs font-bold tracking-[0.12em] uppercase text-[#6B7280] font-ibm">Assigned</p>
        <p className="font-outfit text-2xl font-bold text-[#09090B]">{inbound?.assigned_inbox ?? 0}</p>
      </div>
      <div data-testid="inbound-rejected-stat">
        <p className="text-xs font-bold tracking-[0.12em] uppercase text-[#6B7280] font-ibm">Rejected</p>
        <p className="font-outfit text-2xl font-bold text-red-600">{inbound?.total_rejected ?? 0}</p>
      </div>
    </div>

    <div className="flex items-center gap-4 flex-wrap text-xs font-ibm text-[#6B7280] mb-3">
      <span>Domain: <span className="font-mono text-[#09090B]">{inbound?.inbound_domain || '—'}</span></span>
      <span className={`px-2 py-0.5 rounded border ${inbound?.secret_configured ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
        Secret {inbound?.secret_configured ? 'configured' : 'missing'}
      </span>
      <span>Last received: {inbound?.last_received_at ? new Date(inbound.last_received_at).toLocaleString() : '—'}</span>
      <span>Last reject: {inbound?.last_reject_at ? new Date(inbound.last_reject_at).toLocaleString() : '—'}</span>
    </div>

    {/* Verify result */}
    {verify && (
      <div data-testid="inbound-verify-result"
        className={`p-3 rounded-md border text-sm font-ibm mb-3 ${verify.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
        <div className="flex items-start gap-2">
          {verify.ok ? <CheckCircle size={16} className="mt-0.5 shrink-0 text-green-600" weight="fill" /> : <Warning size={16} className="mt-0.5 shrink-0 text-amber-600" />}
          <div className="min-w-0">
            <p className="font-semibold">{verify.ok ? 'Inbound setup looks correct — MX points to SendGrid and the secret is configured.' : (verify.error || 'Setup incomplete.')}</p>
            {Array.isArray(verify.mx_records) && verify.mx_records.length > 0 && (
              <ul className="mt-1.5 space-y-0.5">
                {verify.mx_records.map((m, i) => (
                  <li key={`${m.exchange}-${i}`} className="font-mono text-xs">{m.preference} {m.exchange}</li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-[#6B7280] mt-1.5">Expected: an MX record on <span className="font-mono">{verify.domain || inbound?.inbound_domain}</span> pointing to <span className="font-mono">mx.sendgrid.net</span>.</p>
          </div>
        </div>
      </div>
    )}

    {/* Recent rejects */}
    {inbound?.recent_rejects?.length > 0 && (
      <div data-testid="inbound-recent-rejects">
        <p className="text-xs font-bold tracking-[0.12em] uppercase text-[#6B7280] font-ibm mb-2">Recent Rejections</p>
        <div className="space-y-1.5 max-h-56 overflow-y-auto">
          {inbound.recent_rejects.map((r, i) => (
            <div key={`${r.received_at}-${i}`} className="flex items-center justify-between gap-3 p-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-md">
              <div className="min-w-0">
                <p className="text-sm font-ibm text-[#09090B] truncate">{r.subject || '(no subject)'}</p>
                <p className="text-xs text-[#6B7280] font-ibm truncate">From: {r.from || '—'} · {r.reason?.replace(/_/g, ' ')}</p>
              </div>
              <span className="text-[11px] text-[#9CA3AF] font-ibm shrink-0">{r.received_at ? new Date(r.received_at).toLocaleString() : ''}</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);
